import { useState, useEffect } from 'react';
import { orderAPI, IMAGE_BASE } from '../../services/api';
import { Package, Eye, Truck, X, ChevronLeft, ChevronRight, Search, Filter, Hash, User, Calendar, CreditCard, MapPin, FileText, Clock, RefreshCw, Upload, Download, Send, ExternalLink } from 'lucide-react';

const paymentMethodLabels = {
    iyzico: 'Kredi Kartı',
    credit_card: 'Kredi Kartı',
    cash_on_delivery: 'Kapıda Ödeme',
    bank_transfer: 'Havale/EFT'
};

const statusConfig = {
    pending:    { label: 'Beklemede',      color: '#f39c12', bg: 'rgba(243,156,18,.1)' },
    paid:       { label: 'Ödendi',         color: '#27ae60', bg: 'rgba(39,174,96,.1)' },
    processing: { label: 'Hazırlanıyor',   color: '#3498db', bg: 'rgba(52,152,219,.1)' },
    shipped:    { label: 'Kargoda',        color: '#9b59b6', bg: 'rgba(155,89,182,.1)' },
    delivered:  { label: 'Teslim Edildi',  color: '#2ecc71', bg: 'rgba(46,204,113,.1)' },
    cancelled:  { label: 'İptal',          color: '#e74c3c', bg: 'rgba(231,76,60,.1)' },
    refunded:   { label: 'İade',           color: '#95a5a6', bg: 'rgba(149,165,166,.1)' },
};

function StatusBadge({ status }) {
    const cfg = statusConfig[status] || { label: status, color: '#999', bg: '#f5f5f5' };
    return (
        <span className="ord-status-badge" style={{ color: cfg.color, background: cfg.bg }}>
            <span className="ord-status-dot" style={{ background: cfg.color }} />
            {cfg.label}
        </span>
    );
}

export default function OrdersAdmin() {
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [updateForm, setUpdateForm] = useState({ status: '', trackingNumber: '', shippingCompany: '', adminNote: '' });
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [invoiceUploading, setInvoiceUploading] = useState(false);
    const [geliverForm, setGeliverForm] = useState({ length: '20', width: '15', height: '10', weight: '1' });
    const [geliverLoading, setGeliverLoading] = useState(false);
    const [geliverResult, setGeliverResult] = useState(null);
    const [arasForm, setArasForm] = useState({ desi: '1', kg: '1', cargoCount: '1', description: '' });
    const [arasLoading, setArasLoading] = useState(false);
    const [arasResult, setArasResult] = useState(null);
    const [courierLoading, setCourierLoading] = useState(false);

    const perPage = 20;

    const loadOrders = async () => {
        setLoading(true);
        try {
            const res = await orderAPI.getAll({ page, status: statusFilter || undefined });
            setOrders(res.data.orders);
            setTotal(res.data.total);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { loadOrders(); }, [page, statusFilter]);

    const openDetail = async (order) => {
        try {
            const res = await orderAPI.getById(order.id);
            setSelectedOrder(res.data);
            setUpdateForm({
                status: res.data.status,
                trackingNumber: res.data.trackingNumber || '',
                shippingCompany: res.data.shippingCompany || '',
                adminNote: res.data.adminNote || ''
            });
            setGeliverResult(res.data.geliverShipmentId ? {
                geliverShipmentId: res.data.geliverShipmentId,
                labelURL: res.data.labelURL,
                trackingNumber: res.data.trackingNumber,
            } : null);
            setArasResult(res.data.shippingCompany === 'Aras Kargo' && res.data.trackingNumber ? {
                trackingNumber: res.data.trackingNumber,
                trackingUrl: res.data.trackingUrl,
                orderNumber: res.data.orderNumber,
            } : null);
        } catch (e) { console.error(e); }
    };

    const handleArasCreate = async () => {
        if (!selectedOrder) return;
        setArasLoading(true);
        try {
            const res = await orderAPI.createArasShipment(selectedOrder.id, arasForm);
            setArasResult(res.data);
            setUpdateForm(f => ({
                ...f,
                trackingNumber: res.data.trackingNumber || f.trackingNumber,
                shippingCompany: 'Aras Kargo',
                status: 'shipped',
            }));
            setSelectedOrder(o => ({ ...o, shippingCompany: 'Aras Kargo', trackingNumber: res.data.trackingNumber, trackingUrl: res.data.trackingUrl }));
            alert('Aras Kargo gönderisi oluşturuldu!');
        } catch (e) {
            alert(e.response?.data?.error || 'Aras Kargo gönderisi oluşturulamadı');
        }
        setArasLoading(false);
    };

    const handleArasLabel = async () => {
        if (!selectedOrder) return;
        try {
            const res = await orderAPI.downloadArasLabel(selectedOrder.id);
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `aras-${selectedOrder.orderNumber}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(e.response?.data?.error || 'Etiket indirilemedi');
        }
    };

    const handleCallCourier = async () => {
        if (!confirm('Aras Kargo kuryesini çağırmak istiyor musunuz?')) return;
        setCourierLoading(true);
        try {
            await orderAPI.callArasCourier();
            alert('Kurye çağrısı gönderildi.');
        } catch (e) {
            alert(e.response?.data?.error || 'Kurye çağrılamadı');
        }
        setCourierLoading(false);
    };

    const handleGeliverCreate = async () => {
        if (!selectedOrder) return;
        setGeliverLoading(true);
        try {
            const res = await orderAPI.createGeliverShipment(selectedOrder.id, geliverForm);
            setGeliverResult(res.data);
            setUpdateForm(f => ({
                ...f,
                trackingNumber: res.data.trackingNumber || f.trackingNumber,
                shippingCompany: res.data.providerName || f.shippingCompany,
                status: 'shipped',
            }));
            setSelectedOrder(o => ({ ...o, geliverShipmentId: res.data.geliverShipmentId, labelURL: res.data.labelURL }));
            alert('Geliver gönderisi oluşturuldu!');
        } catch (e) {
            alert(e.response?.data?.error || 'Geliver gönderisi oluşturulamadı');
        }
        setGeliverLoading(false);
    };

    const handleUpdate = async () => {
        setUpdating(true);
        try {
            await orderAPI.updateStatus(selectedOrder.id, updateForm);
            setSelectedOrder(null);
            loadOrders();
        } catch (e) { alert(e.response?.data?.error || 'Hata oluştu'); }
        setUpdating(false);
    };

    const handleInvoiceUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setInvoiceUploading(true);
        try {
            const formData = new FormData();
            formData.append('invoiceFile', file);
            await orderAPI.uploadInvoice(selectedOrder.id, formData);
            const res = await orderAPI.getById(selectedOrder.id);
            setSelectedOrder(res.data);
        } catch (err) {
            alert(err.response?.data?.error || 'Fatura yüklenemedi');
        }
        setInvoiceUploading(false);
    };

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);
    const formatDate = (d) => new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const parseAddr = (json) => { try { return JSON.parse(json); } catch { return null; } };

    const totalPages = Math.ceil(total / perPage);

    const filtered = searchTerm
        ? orders.filter(o =>
            o.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            `${o.customer?.firstName} ${o.customer?.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : orders;

    return (
        <div className="ord-page">
            {/* Header */}
            <div className="ord-header">
                <div className="ord-header-left">
                    <h2><Package size={22} /> Siparişler</h2>
                    <span className="ord-count">{total}</span>
                </div>
                <button className="ord-refresh-btn" onClick={loadOrders} title="Yenile"><RefreshCw size={16} /></button>
            </div>

            {/* Toolbar */}
            <div className="ord-toolbar">
                <div className="ord-search">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Sipariş no veya müşteri ara..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="ord-filters">
                    <Filter size={14} />
                    <button className={`ord-filter-chip ${statusFilter === '' ? 'active' : ''}`} onClick={() => { setStatusFilter(''); setPage(1); }}>Tümü</button>
                    {Object.entries(statusConfig).map(([key, cfg]) => (
                        <button
                            key={key}
                            className={`ord-filter-chip ${statusFilter === key ? 'active' : ''}`}
                            onClick={() => { setStatusFilter(key); setPage(1); }}
                        >
                            <span className="ord-filter-dot" style={{ background: cfg.color }} />
                            {cfg.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="ord-loading"><div className="spinner" /></div>
            ) : filtered.length === 0 ? (
                <div className="ord-empty">
                    <Package size={48} strokeWidth={1} />
                    <p>Sipariş bulunamadı</p>
                </div>
            ) : (
                <>
                    <div className="ord-table-wrap">
                        <table className="ord-table">
                            <thead>
                                <tr>
                                    <th><Hash size={13} /> Sipariş No</th>
                                    <th><User size={13} /> Müşteri</th>
                                    <th><CreditCard size={13} /> Tutar</th>
                                    <th>Ödeme</th>
                                    <th>Durum</th>
                                    <th><Calendar size={13} /> Tarih</th>
                                    <th>İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(order => (
                                    <tr key={order.id} onClick={() => openDetail(order)}>
                                        <td><span className="ord-number">#{order.orderNumber}</span></td>
                                        <td>
                                            <div className="ord-customer">
                                                <div className="ord-customer-avatar">
                                                    {order.customer?.firstName?.[0]}{order.customer?.lastName?.[0]}
                                                </div>
                                                <div>
                                                    <span className="ord-customer-name">{order.customer ? `${order.customer.firstName} ${order.customer.lastName}` : '-'}</span>
                                                    <span className="ord-customer-email">{order.customer?.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="ord-amount">{formatPrice(order.totalAmount)}</span></td>
                                        <td><span className="ord-payment-method">{paymentMethodLabels[order.paymentMethod] || order.paymentMethod}</span></td>
                                        <td><StatusBadge status={order.status} /></td>
                                        <td><span className="ord-date">{formatDate(order.createdAt)}</span></td>
                                        <td>
                                            <button className="ord-detail-btn" onClick={(e) => { e.stopPropagation(); openDetail(order); }}>
                                                <Eye size={15} /> Detay
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="ord-pagination">
                            <span className="ord-pagination-info">
                                {(page - 1) * perPage + 1}-{Math.min(page * perPage, total)} / {total} sipariş
                            </span>
                            <div className="ord-pagination-btns">
                                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                    <ChevronLeft size={16} />
                                </button>
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                    let p;
                                    if (totalPages <= 5) p = i + 1;
                                    else if (page <= 3) p = i + 1;
                                    else if (page >= totalPages - 2) p = totalPages - 4 + i;
                                    else p = page - 2 + i;
                                    return (
                                        <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>
                                            {p}
                                        </button>
                                    );
                                })}
                                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Order Detail Modal */}
            {selectedOrder && (
                <div className="ord-modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="ord-modal" onClick={e => e.stopPropagation()}>
                        <div className="ord-modal-head">
                            <div>
                                <h3>Sipariş #{selectedOrder.orderNumber}</h3>
                                <span className="ord-modal-date"><Clock size={13} /> {formatDate(selectedOrder.createdAt)}</span>
                            </div>
                            <div className="ord-modal-head-right">
                                <StatusBadge status={selectedOrder.status} />
                                <button className="ord-modal-close" onClick={() => setSelectedOrder(null)}><X size={20} /></button>
                            </div>
                        </div>

                        <div className="ord-modal-body">
                            {/* Info Cards */}
                            <div className="ord-info-grid">
                                <div className="ord-info-card">
                                    <div className="ord-info-card-icon" style={{ background: 'rgba(52,152,219,.1)', color: '#3498db' }}><User size={18} /></div>
                                    <div>
                                        <label>Müşteri</label>
                                        <strong>{selectedOrder.customer?.firstName} {selectedOrder.customer?.lastName}</strong>
                                        <span>{selectedOrder.customer?.email}</span>
                                        <span>{selectedOrder.customer?.phone}</span>
                                    </div>
                                </div>
                                <div className="ord-info-card">
                                    <div className="ord-info-card-icon" style={{ background: 'rgba(155,89,182,.1)', color: '#9b59b6' }}><MapPin size={18} /></div>
                                    <div>
                                        <label>Teslimat Adresi</label>
                                        {(() => {
                                            const addr = parseAddr(selectedOrder.shippingAddress);
                                            if (!addr) return <span>-</span>;
                                            return (
                                                <>
                                                    <strong>{addr.firstName} {addr.lastName}</strong>
                                                    <span>{addr.address}</span>
                                                    <span>{addr.district}, {addr.city}</span>
                                                    <span>{addr.phone}</span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div className="ord-info-card">
                                    <div className="ord-info-card-icon" style={{ background: 'rgba(243,156,18,.1)', color: '#f39c12' }}><CreditCard size={18} /></div>
                                    <div>
                                        <label>Ödeme Yöntemi</label>
                                        <strong>{paymentMethodLabels[selectedOrder.paymentMethod] || selectedOrder.paymentMethod}</strong>
                                        {selectedOrder.identityNumber && <span>TC Kimlik: {selectedOrder.identityNumber}</span>}
                                    </div>
                                </div>
                                <div className="ord-info-card">
                                    <div className="ord-info-card-icon" style={{ background: 'rgba(39,174,96,.1)', color: '#27ae60' }}><FileText size={18} /></div>
                                    <div>
                                        <label>Fatura Bilgileri</label>
                                        <strong>{selectedOrder.invoiceType === 'corporate' ? 'Kurumsal Fatura' : 'Bireysel Fatura'}</strong>
                                        {selectedOrder.invoiceType === 'corporate' && (
                                            <>
                                                <span>Firma: {selectedOrder.companyName}</span>
                                                <span>Vergi Dairesi: {selectedOrder.taxOffice}</span>
                                                <span>Vergi No: {selectedOrder.taxNumber}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Items */}
                            <div className="ord-items-section">
                                <h4><Package size={16} /> Ürünler</h4>
                                <div className="ord-items-list">
                                    {(selectedOrder.items || []).map(item => (
                                        <div key={item.id} className="ord-item-row">
                                            {item.productImage ? (
                                                <img src={`${IMAGE_BASE}/${item.productImage}`} alt="" className="ord-item-img" />
                                            ) : (
                                                <div className="ord-item-img ord-item-img-placeholder"><Package size={20} /></div>
                                            )}
                                            <div className="ord-item-info">
                                                <span className="ord-item-name">{item.productName}</span>
                                                {item.variantInfo && <span className="ord-item-variant">{item.variantInfo}</span>}
                                            </div>
                                            <span className="ord-item-qty">{item.quantity}x</span>
                                            <span className="ord-item-price">{formatPrice(item.unitPrice)}</span>
                                            <span className="ord-item-total">{formatPrice(item.totalPrice)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="ord-items-total">
                                    {parseFloat(selectedOrder.discountAmount) > 0 && (
                                        <>
                                            <div className="ord-items-subtotal">
                                                <span>Ara Toplam</span>
                                                <span>{formatPrice(parseFloat(selectedOrder.totalAmount) + parseFloat(selectedOrder.discountAmount))}</span>
                                            </div>
                                            <div className="ord-items-discount">
                                                <span>Kupon ({selectedOrder.couponCode})</span>
                                                <span style={{color:'var(--danger)'}}>-{formatPrice(selectedOrder.discountAmount)}</span>
                                            </div>
                                        </>
                                    )}
                                    <span>Toplam</span>
                                    <strong>{formatPrice(selectedOrder.totalAmount)}</strong>
                                </div>
                            </div>

                            {/* Geliver Kargo Section */}
                            <div className="ord-update-section">
                                <h4><Send size={16} /> Geliver ile Kargo Oluştur</h4>
                                {geliverResult?.geliverShipmentId ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <div style={{ padding: '0.75rem 1rem', background: 'rgba(39,174,96,.1)', borderRadius: '8px', color: '#27ae60', fontWeight: 500, fontSize: '0.875rem' }}>
                                            Geliver gönderisi aktif — ID: {geliverResult.geliverShipmentId}
                                        </div>
                                        {(geliverResult.trackingNumber || geliverResult.barcode) && (
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                Takip No: <strong>{geliverResult.trackingNumber || geliverResult.barcode}</strong>
                                            </div>
                                        )}
                                        {(geliverResult.labelURL || selectedOrder.labelURL) && (
                                            <a href={geliverResult.labelURL || selectedOrder.labelURL} target="_blank" rel="noopener noreferrer" className="ord-btn-download" style={{ alignSelf: 'flex-start' }}>
                                                <Download size={14} /> Kargo Etiketini İndir
                                            </a>
                                        )}
                                        {geliverResult.trackingUrl && (
                                            <a href={geliverResult.trackingUrl} target="_blank" rel="noopener noreferrer" className="ord-btn-download" style={{ alignSelf: 'flex-start' }}>
                                                <ExternalLink size={14} /> Takip Sayfası
                                            </a>
                                        )}
                                    </div>
                                ) : (
                                    <div className="ord-update-grid">
                                        <div className="ord-field">
                                            <label>Uzunluk (cm)</label>
                                            <input type="number" value={geliverForm.length} onChange={e => setGeliverForm(f => ({ ...f, length: e.target.value }))} min="1" />
                                        </div>
                                        <div className="ord-field">
                                            <label>Genişlik (cm)</label>
                                            <input type="number" value={geliverForm.width} onChange={e => setGeliverForm(f => ({ ...f, width: e.target.value }))} min="1" />
                                        </div>
                                        <div className="ord-field">
                                            <label>Yükseklik (cm)</label>
                                            <input type="number" value={geliverForm.height} onChange={e => setGeliverForm(f => ({ ...f, height: e.target.value }))} min="1" />
                                        </div>
                                        <div className="ord-field">
                                            <label>Ağırlık (kg)</label>
                                            <input type="number" value={geliverForm.weight} onChange={e => setGeliverForm(f => ({ ...f, weight: e.target.value }))} min="0.1" step="0.1" />
                                        </div>
                                        <div className="ord-field" style={{ gridColumn: '1 / -1' }}>
                                            <button className="ord-btn-save" onClick={handleGeliverCreate} disabled={geliverLoading} style={{ width: 'auto' }}>
                                                <Send size={15} /> {geliverLoading ? 'Gönderiliyor...' : 'Geliver\'a Kargo Oluştur'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Aras Kargo Section */}
                            <div className="ord-update-section">
                                <h4><Truck size={16} /> Aras Kargo Gönderi</h4>
                                {arasResult?.trackingNumber ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <div style={{ padding: '0.75rem 1rem', background: 'rgba(231,76,60,.08)', borderRadius: '8px', color: '#c0392b', fontWeight: 500, fontSize: '0.875rem' }}>
                                            Aras Kargo gönderisi aktif — Takip No: <strong>{arasResult.trackingNumber}</strong>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <button className="ord-btn-download" onClick={handleArasLabel} style={{ alignSelf: 'flex-start' }}>
                                                <Download size={14} /> Etiket İndir (PDF)
                                            </button>
                                            {arasResult.trackingUrl && (
                                                <a href={arasResult.trackingUrl} target="_blank" rel="noopener noreferrer" className="ord-btn-download">
                                                    <ExternalLink size={14} /> Aras Takip Sayfası
                                                </a>
                                            )}
                                            <button className="ord-btn-download" onClick={handleCallCourier} disabled={courierLoading}>
                                                <Send size={14} /> {courierLoading ? 'Çağrılıyor...' : 'Kurye Çağır'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="ord-update-grid">
                                        <div className="ord-field">
                                            <label>Desi</label>
                                            <input type="number" value={arasForm.desi} onChange={e => setArasForm(f => ({ ...f, desi: e.target.value }))} min="0.1" step="0.1" />
                                        </div>
                                        <div className="ord-field">
                                            <label>Ağırlık (kg)</label>
                                            <input type="number" value={arasForm.kg} onChange={e => setArasForm(f => ({ ...f, kg: e.target.value }))} min="0.1" step="0.1" />
                                        </div>
                                        <div className="ord-field">
                                            <label>Koli Sayısı</label>
                                            <input type="number" value={arasForm.cargoCount} onChange={e => setArasForm(f => ({ ...f, cargoCount: e.target.value }))} min="1" />
                                        </div>
                                        <div className="ord-field">
                                            <label>Açıklama</label>
                                            <input value={arasForm.description} onChange={e => setArasForm(f => ({ ...f, description: e.target.value }))} placeholder="Opsiyonel" />
                                        </div>
                                        <div className="ord-field" style={{ gridColumn: '1 / -1' }}>
                                            <button className="ord-btn-save" onClick={handleArasCreate} disabled={arasLoading} style={{ width: 'auto' }}>
                                                <Truck size={15} /> {arasLoading ? 'Gönderiliyor...' : 'Aras Kargo\'ya Gönderi Oluştur'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Update Section */}
                            <div className="ord-update-section">
                                <h4><Truck size={16} /> Sipariş Güncelle</h4>
                                <div className="ord-update-grid">
                                    <div className="ord-field">
                                        <label>Durum</label>
                                        <select value={updateForm.status} onChange={e => setUpdateForm({ ...updateForm, status: e.target.value })}>
                                            {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="ord-field">
                                        <label>Kargo Firması</label>
                                        <input value={updateForm.shippingCompany} onChange={e => setUpdateForm({ ...updateForm, shippingCompany: e.target.value })} placeholder="Yurtiçi, Aras, MNG..." />
                                    </div>
                                    <div className="ord-field">
                                        <label>Takip No</label>
                                        <input value={updateForm.trackingNumber} onChange={e => setUpdateForm({ ...updateForm, trackingNumber: e.target.value })} placeholder="Kargo takip numarası" />
                                    </div>
                                    <div className="ord-field">
                                        <label>Admin Notu</label>
                                        <input value={updateForm.adminNote} onChange={e => setUpdateForm({ ...updateForm, adminNote: e.target.value })} placeholder="İç not (müşteri görmez)" />
                                    </div>
                                    <div className="ord-field" style={{ gridColumn: '1 / -1' }}>
                                        <label>Fatura Dosyası (PDF/Resim)</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <label className="ord-btn-upload" style={{ cursor: 'pointer' }}>
                                                <Upload size={14} /> {invoiceUploading ? 'Yükleniyor...' : 'Fatura Yükle'}
                                                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleInvoiceUpload} hidden disabled={invoiceUploading} />
                                            </label>
                                            {selectedOrder.invoiceFile && (
                                                <a href={selectedOrder.invoiceFile} target="_blank" rel="noopener noreferrer" className="ord-btn-download">
                                                    <Download size={14} /> Faturayı Görüntüle
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="ord-modal-foot">
                            <button className="ord-btn-cancel" onClick={() => setSelectedOrder(null)}>Kapat</button>
                            <button className="ord-btn-save" onClick={handleUpdate} disabled={updating}>
                                <Truck size={16} /> {updating ? 'Kaydediliyor...' : 'Güncelle'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
